import { Input, TableColumnsType } from 'antd';
import usePagination from 'hooks/usePagination';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CatEventTableData } from './type';
import ContentIcon from 'assets/icons/content';
import { useQuery } from 'react-query';
import { catEventApi } from 'services/apis';
import { useNavigate } from 'react-router';
import CustomButton from 'components/ui/custom-button';
import { EButtonVariant } from 'components/ui/custom-button/enums';
import { AddIcon } from 'assets/icons/add';
import { ENavigationPath } from 'constants/path';
import SearchIcon from 'assets/icons/search';
import CustomTable from 'components/ui/custom-table';

export default function CatEventTable() {
  const [inputText, setInputText] = useState('');
  const [searchText, setSearchText] = useState('');
  const { currentPage, pageSize, onPageChange, onShowSizeChange } =
    usePagination();

  const { t } = useTranslation();

  const columns: TableColumnsType<CatEventTableData> = useMemo(
    () => [
      {
        key: 'index',
        title: <ContentIcon />,
        dataIndex: 'index',
        width: '1%',
        align: 'center',
        render: (index: number) => index + 1,
      },
      {
        key: 'name',
        title: t('catEvent.eventName'),
        dataIndex: 'name',
        width: '15%',
        align: 'left',
        render: (text: string) => (
          <div
            style={{
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              maxWidth: '15vw',
            }}
          >
            {text}
          </div>
        ),
      },
      {
        key: 'fromDate',
        title: t('catEvent.fromDate'),
        dataIndex: 'fromDate',
        width: '15%',
        align: 'left',
      },
      {
        key: 'toDate',
        title: t('catEvent.toDate'),
        dataIndex: 'toDate',
        width: '15%',
        align: 'left',
      },
      {
        key: 'country',
        title: t('general.country'),
        dataIndex: 'country',
        width: '15%',
        align: 'left',
      },
    ],
    [t]
  );

  const {
    data: { items: catEventData, pagination: { total } } = {
      items: [],
      pagination: { total: 0 },
    },
  } = useQuery(['catEvents', pageSize, currentPage, searchText], async () => {
    const { items, pagination } = await catEventApi.filterCatEvents({
      page: currentPage,
      pageSize,
      searchText: searchText,
    });
    return {
      items: items.map((catEvent, index) => ({
        index: pageSize * (currentPage - 1) + index,
        key: catEvent.catEventId,
        name: catEvent.eventName,
        fromDate: catEvent.fromDate,
        toDate: catEvent.toDate,
        country: catEvent.country,
        catEventId: catEvent.catEventId,
        description: catEvent.description,
      })),
      pagination,
    };
  });
  const navigate = useNavigate();

  const handleSearchTrigger = () => setSearchText(inputText);

  return (
    <div className="py-4 w-full">
      <div className="flex justify-between items-center mb-4">
        <div className="text-2xl font-medium">
          {t('catEvent.selectCatCode')}
        </div>
        <CustomButton
          className="w-1/5"
          variant={EButtonVariant.PRIMARY}
          label={t('general.createNewRecord')}
          prefixIcon={<AddIcon />}
          onClick={() => navigate(ENavigationPath.NEW_CAT_EVENT)}
        />
      </div>
      <div className="flex gap-2 items-center mb-4">
        <Input
          placeholder={t('catEvent.searchCatEventIfExists')}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          className="flex-grow py-[8px]"
          prefix={<SearchIcon />}
          onPressEnter={handleSearchTrigger}
        />
        <CustomButton
          className="w-2/12 py-[8px]"
          variant={EButtonVariant.PRIMARY}
          label={t('general.search')}
          onClick={handleSearchTrigger}
        />
      </div>

      <CustomTable
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onShowSizeChange={onShowSizeChange}
        columns={columns}
        dataSource={catEventData || []}
        total={total || 0}
        onRow={(record) => ({
          onClick: () =>
            navigate(
              ENavigationPath.UPDATE_CAT_EVENT.replace(':id', record.catEventId)
            ),
          className: 'cursor-pointer hover:bg-gray-50',
        })}
      />
    </div>
  );
}
